import { CONSTANTS } from '../Constants'

class LocalCacheClass {
    private _store: ICacheEntry[] = []
    private _prefix = `vbc${CONSTANTS.frontend.version}:`

    init = () => {
        if (window && sessionStorage) {
            // check if previous cache should be purged
            this._checkValidity()

            this._store = []
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i)
                if (key && key.length > 0 && key.startsWith(this._prefix))
                    this._store.push({ key: key.replace(this._prefix, ''), value: JSON.parse(sessionStorage.getItem(key)), stale: true })
            }
        }
    }

    get = (key: string): ICacheEntry => this._store.find(x => x.key === key)
    getLike = (key: string): ICacheEntry[] => this._store.filter(x => x.key.startsWith(key))
    getIndex = (key: string): number => this._store.findIndex(x => x.key === key)

    set = <T>(key: string, value: T) => {
        const prev = this.getIndex(key)

        if (prev && prev > -1) this._store.splice(prev, 1, { key, value })
        else this._store.push({ key, value })

        sessionStorage.setItem(this._prefix + key, JSON.stringify(value))
    }

    setStale = (key: string) => {
        const index = this._store.findIndex(x => x.key === key)
        if (index >= 0) {
            this._store[index] = { ...this._store[index], stale: true }
        }
    }

    private _checkValidity = () => {
        const cacheVersionKey = 'cache:version'
        const cacheVersion = sessionStorage.getItem(cacheVersionKey) ?? 'vbc:'

        if (cacheVersion !== this._prefix) {
            const localStorageKeys = Array(sessionStorage.length).fill('').map((_, i) => sessionStorage.key(i))

            for (let i = 0; i < localStorageKeys.length; i++) {
                const key = localStorageKeys[i]
                if (key && key.length > 0 && key.startsWith(cacheVersion)) {
                    sessionStorage.removeItem(key)
                }
            }
        }

        sessionStorage.setItem(cacheVersionKey, this._prefix)
    }

    clearAll = () => {
        const localStorageKeys = Array(sessionStorage.length)
            .fill('')
            .map((_, i) => sessionStorage.key(i))

        for (const key of localStorageKeys) {
            if (key && key.startsWith(this._prefix)) {
                sessionStorage.removeItem(key)
            }
        }
        this._store = []
    }

    clear = (key: string) => {
        if (key.includes('*')) {
            const keySuffix = key.split('/*')[0]
            const allToClear = this.getLike(keySuffix)
            for (let i = 0; i < allToClear.length; i++) {
                const toClear = allToClear[i]
                sessionStorage.removeItem(this._prefix + toClear.key)
                this._store = this._store.filter(x => !x.key.startsWith(keySuffix))
            }
        } else {
            sessionStorage.removeItem(key)
            this._store = this._store.filter(x => x.key !== key)
        }
    }

    delete = (key: string) => {
        sessionStorage.removeItem(this._prefix + key)
        this._store = this._store.filter(x => x.key !== key)
    }
}

const LocalCache = new LocalCacheClass()
export default LocalCache

export interface ICacheEntry {
    key: string
    value: any
    /**
     * indicates wheter the value is not longer considerable correct.
     * this means that the api call associated with this value (if any) should be remade and this value needs to be updated.
     */
    stale?: boolean
}
